import { GUARDRAILS_OFFERED } from '../../engine/bench/guardrails-flags.ts';
import { KEEP_TIERS } from '../../engine/bench/keep.ts';
/**
 * The cost confirmation dialog (BENCH-V2-SPEC §13.4): the model, the effort, the expected and maximum cost including
 * reasoning, and for Economy the rounds and the "up to 24 hours per round" warning.
 */
import {
	type AllowedModel,
	EFFORT_LEVELS,
	type Effort,
	LATEST_HAIKU,
	LATEST_SONNET,
	MODEL_ALLOWLIST,
} from '../../shared/models.ts';
import { fmt2, fmtInt } from '../format.ts';
import { COST } from '../strings-bench.ts';
import { BENCH_EXPLAIN, RECOMMEND } from '../strings-explain.ts';
import { Dialog } from '../ui.tsx';
import {
	cancelCostDialog,
	confirmCost,
	setConcurrency,
	setEffort,
	setGuardrails,
	setKeep,
	setModel,
	setVersions,
} from './controller.ts';
import {
	CONCURRENCY_CHOICES,
	VERSIONS_CHOICES,
	concurrency,
	costDialogOpen,
	costEstimate,
	economy,
	effort,
	guardrails,
	keep,
	model,
	versions,
} from './state.ts';

export function CostDialog() {
	const est = costEstimate.value;
	if (!est) return null;
	return (
		<Dialog
			title={COST.dialogTitle}
			open={costDialogOpen.value}
			onClose={cancelCostDialog}
			actions={
				<>
					<button type="button" class="btn btn-secondary" onClick={cancelCostDialog}>
						{COST.cancel}
					</button>
					<button
						type="button"
						class="btn btn-primary"
						data-testid="bench-cost-confirm"
						onClick={() => void confirmCost()}
					>
						{COST.confirm}
					</button>
				</>
			}
		>
			<dl class="kv" data-testid="bench-cost">
				<div>
					<dt>
						<label for="bench-model">{COST.model}</label>
					</dt>
					<dd>
						<select
							id="bench-model"
							data-testid="bench-model"
							value={model.value}
							onChange={(e) => setModel((e.target as HTMLSelectElement).value as AllowedModel)}
						>
							{MODEL_ALLOWLIST.map((m) => (
								<option key={m} value={m}>
									{m === LATEST_SONNET ? COST.modelNames.sonnet : COST.modelNames.haiku}
								</option>
							))}
						</select>
						<span class="mono dim"> {est.model}</span>
						{model.value === LATEST_HAIKU ? (
							<p class="dim" data-testid="bench-haiku-time">
								{COST.haikuTime}
							</p>
						) : null}
					</dd>
				</div>
				<div>
					<dd class="dim">{RECOMMEND.model}</dd>
				</div>
				<div>
					<dt>
						<label for="bench-effort">{COST.effort}</label>
					</dt>
					<dd>
						<select
							id="bench-effort"
							data-testid="bench-effort"
							value={effort.value}
							onChange={(e) => setEffort((e.target as HTMLSelectElement).value as Effort)}
						>
							{EFFORT_LEVELS.map((l) => (
								<option key={l} value={l}>
									{l === RECOMMEND.effort.toLowerCase()
										? `${COST.efforts[l]} ${RECOMMEND.recommendedTag}`
										: COST.efforts[l]}
								</option>
							))}
						</select>
						<div data-testid="bench-recommend">
							<strong>{RECOMMEND.effortLine}</strong>
							<p class="dim">{RECOMMEND.why}</p>
							{effort.value !== 'high' ? (
								<>
									<ul class="dim">
										<li>{RECOMMEND.alternatives[effort.value]}</li>
									</ul>
									<p class="dim">{RECOMMEND.chosenNote(COST.efforts[effort.value])}</p>
									<button type="button" class="btn-text" onClick={() => setEffort('high')}>
										{RECOMMEND.useRecommended}
									</button>
								</>
							) : null}
						</div>
					</dd>
				</div>
				<div>
					<dt>
						<label for="bench-versions">{COST.versions}</label>
					</dt>
					<dd>
						<select
							id="bench-versions"
							data-testid="bench-versions"
							value={String(versions.value)}
							onChange={(e) => setVersions(Number((e.target as HTMLSelectElement).value))}
						>
							{VERSIONS_CHOICES.map((n) => (
								<option key={n} value={String(n)}>
									{COST.versionsOption(n)}
								</option>
							))}
						</select>
						<p class="dim">{COST.versionsHint}</p>
					</dd>
				</div>
				<div>
					<dt>{COST.keep}</dt>
					<dd>
						<div role="radiogroup" aria-label={COST.keep} class="radio-row">
							{KEEP_TIERS.map((t) => (
								<label key={t}>
									<input
										type="radio"
										name="bench-keep"
										data-testid={`bench-keep-${t}`}
										checked={keep.value === t}
										onChange={() => setKeep(t)}
									/>{' '}
									{COST.keepOptions[t]}
								</label>
							))}
						</div>
						<p class="dim">{COST.keepHint}</p>
					</dd>
				</div>
				{GUARDRAILS_OFFERED ? (
					<div>
						<dt>{COST.guardrails}</dt>
						<dd>
							<label>
								<input
									type="checkbox"
									data-testid="bench-guardrails"
									checked={guardrails.value}
									onChange={(e) => setGuardrails((e.target as HTMLInputElement).checked)}
								/>{' '}
								{COST.guardrailsLabel}
							</label>
							<p class="dim">{COST.guardrailsHint}</p>
						</dd>
					</div>
				) : null}
				{economy.value ? null : (
					<div>
						<dt>
							<label for="bench-concurrency">{COST.concurrency}</label>
						</dt>
						<dd>
							<select
								id="bench-concurrency"
								data-testid="bench-concurrency"
								value={String(concurrency.value)}
								onChange={(e) => setConcurrency(Number((e.target as HTMLSelectElement).value))}
							>
								{CONCURRENCY_CHOICES.map((n) => (
									<option key={n} value={String(n)}>
										{COST.concurrencyOption(n)}
									</option>
								))}
							</select>
							<p class="dim">{COST.concurrencyHint}</p>
						</dd>
					</div>
				)}
				<div>
					<dt>{COST.chunks(fmtInt(est.chunks))}</dt>
					<dd />
				</div>
				{est.briefCalls > 0 ? (
					<>
						<div>
							<dt>{COST.brief(String(est.briefCalls))}</dt>
							<dd data-testid="bench-cost-brief">${fmt2(est.parts.brief)}</dd>
						</div>
						<div>
							<dd class="dim">{COST.briefHint}</dd>
						</div>
					</>
				) : null}
				<div>
					<dt>{COST.expected}</dt>
					<dd data-testid="bench-cost-expected">${fmt2(est.expectedUsd)}</dd>
				</div>
				<div>
					<dd class="dim">{BENCH_EXPLAIN.expectedCost}</dd>
				</div>
				<div>
					<dt>{COST.max(fmtInt(3))}</dt>
					<dd data-testid="bench-cost-max">${fmt2(est.maxUsd)}</dd>
				</div>
			</dl>
			{economy.value ? (
				<p class="dim">{COST.economyNote}</p>
			) : (
				<p class="dim" data-testid="bench-standard-note">
					{COST.standardNote}
				</p>
			)}
		</Dialog>
	);
}
